import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Loader2,
  RefreshCw,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react";

import {
  aiApi,
  type AcceptGraphSuggestionResponse,
  type AiSource,
  type AnalyzeGraphResponse,
  type GraphAnalysisMissingLink,
  type GraphAnalysisMissingLinkKind,
  type GraphAnalysisStructuralInsight,
} from "lib/api/ai";
import { getAiUiStrings, resolveAiLocale } from "i18n/aiUi";
import LiveAnnouncer from "ui/LiveAnnouncer";

type FeedbackState = -1 | 1 | null;

export interface GraphAnalysisPanelProps {
  /** Target graph collection id. Required. */
  collectionId: number;
  /** Current node count on the canvas (used to gate the analyze button). */
  nodeCount: number;
  /** Current edge count on the canvas (used to gate the analyze button). */
  edgeCount: number;
  /** Called when the user dismisses the panel. */
  onClose: () => void;
  /**
   * Called after a missing-link suggestion was successfully accepted.
   * The parent should refetch the snapshot (or patch local state) so the
   * new node + edge render on the canvas.
   */
  onSuggestionAccepted: (payload: AcceptGraphSuggestionResponse) => void;
  /**
   * Optional callback the parent can use to highlight the node in the
   * canvas when the user taps an anchor or insight chip.
   */
  onFocusNode?: (nodeId: number) => void;
}

interface GroupedMissingLinks {
  kind: GraphAnalysisMissingLinkKind;
  groupLabel: string;
  items: GraphAnalysisMissingLink[];
}

const GROUP_ORDER: GraphAnalysisMissingLinkKind[] = ["bridge", "upstream", "downstream", "lever"];

/**
 * Group validated missing-link suggestions by `kind` so the UI can render
 * them in meaningful sections (bridges, upstream drivers, downstream
 * effects, mechanisms/levers). Empty groups are omitted.
 */
function groupMissingLinks(
  missingLinks: GraphAnalysisMissingLink[],
  labels: Record<GraphAnalysisMissingLinkKind, string>
): GroupedMissingLinks[] {
  const grouped: GroupedMissingLinks[] = [];
  for (const kind of GROUP_ORDER) {
    const items = missingLinks.filter((entry) => entry.kind === kind);
    if (items.length > 0) {
      grouped.push({ groupLabel: labels[kind], items, kind });
    }
  }
  return grouped;
}

/**
 * Render a compact Wikidata/Wikipedia chip row beneath a suggestion card.
 */
const EntityChips: React.FC<{
  wikipediaTitle: string;
  wikipediaUrl: string;
  wikidataQid: string | null;
  wikidataChipLabel: (qid: string) => string;
}> = ({ wikipediaTitle, wikipediaUrl, wikidataQid, wikidataChipLabel }) => (
  <div className="flex flex-wrap items-center gap-2">
    <a
      href={wikipediaUrl}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 rounded-full border border-eoe-border-main bg-eoe-bg px-2.5 py-0.5 text-[11px] font-medium text-eoe-text-sub transition-colors hover:text-eoe-text dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
    >
      <ExternalLink className="size-3" aria-hidden />
      <span>{wikipediaTitle}</span>
    </a>
    {wikidataQid ? (
      <a
        href={`https://www.wikidata.org/wiki/${wikidataQid}`}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 rounded-full border border-eoe-border-main bg-eoe-bg px-2.5 py-0.5 text-[11px] font-medium text-eoe-text-sub transition-colors hover:text-eoe-text dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
      >
        {wikidataChipLabel(wikidataQid)}
      </a>
    ) : null}
  </div>
);

/**
 * Render the list of source URLs attached to a suggestion or root-cause
 * candidate. Rendered as clickable chips with publisher labels.
 */
const SourceChips: React.FC<{ label: string; sources: AiSource[] }> = ({ label, sources }) => {
  if (!sources || sources.length === 0) {
    return null;
  }
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub">
        {label}
      </p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {sources.map((source) => (
          <a
            key={source.url}
            href={source.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex max-w-full items-center gap-1 rounded-full border border-eoe-border-main px-2.5 py-0.5 text-[11px] text-eoe-text-sub transition-colors hover:text-eoe-text dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
          >
            <span className="truncate">{source.title || source.publisher || source.url}</span>
          </a>
        ))}
      </div>
    </div>
  );
};

/**
 * Panel mounted inside the graph editor canvas that surfaces the
 * `analyze_graph` AI capability output: structural insights, root-cause
 * candidates, and validated missing-link suggestions that route through
 * the canonical accept flow for a safe add-to-graph action.
 */
const GraphAnalysisPanel: React.FC<GraphAnalysisPanelProps> = ({
  collectionId,
  nodeCount,
  edgeCount,
  onClose,
  onSuggestionAccepted,
  onFocusNode,
}) => {
  const locale = useMemo(() => resolveAiLocale(), []);
  const ui = useMemo(() => getAiUiStrings(locale), [locale]);
  const copy = ui.analyzeGraph;

  const [response, setResponse] = useState<AnalyzeGraphResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [acceptedKeys, setAcceptedKeys] = useState<Set<string>>(new Set());
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(new Set());
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);
  const [feedbackStateByKey, setFeedbackStateByKey] = useState<Record<string, FeedbackState>>({});
  const [submittingFeedbackKey, setSubmittingFeedbackKey] = useState<string | null>(null);
  const [liveMessage, setLiveMessage] = useState<string>("");

  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    setResponse(null);
    setLoadError(null);
    setAcceptError(null);
    setAcceptedKeys(new Set());
    setDismissedKeys(new Set());
    setSubmittingKey(null);
    setFeedbackStateByKey({});
  }, [collectionId]);

  const canAnalyze = nodeCount >= 2 && edgeCount >= 1;

  const runAnalyze = useCallback(async () => {
    if (!canAnalyze || isLoading) {
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    setAcceptError(null);
    try {
      const payload = await aiApi.analyzeGraph({
        collectionId,
        locale,
      });
      setResponse(payload);
      setAcceptedKeys(new Set());
      setDismissedKeys(new Set());
      setFeedbackStateByKey({});
      setLiveMessage(
        copy.announceReady(
          payload.structuralInsights.length,
          payload.missingLinks.length
        )
      );
    } catch (caughtError: unknown) {
      const serverMessage = typeof caughtError === "object"
        && caughtError
        && "response" in caughtError
        ? String(
            ((caughtError as { response?: { data?: { message?: string } } }).response?.data?.message)
            || copy.errorState
          )
        : copy.errorState;
      setLoadError(serverMessage);
    } finally {
      setIsLoading(false);
    }
  }, [canAnalyze, collectionId, copy, isLoading, locale]);

  const handleAccept = useCallback(async (suggestion: GraphAnalysisMissingLink) => {
    if (!response || submittingKey) {
      return;
    }
    setSubmittingKey(suggestion.suggestionKey);
    setAcceptError(null);
    try {
      const acceptResponse = await aiApi.acceptGraphSuggestion({
        collectionId,
        invocationId: response.meta.invocationId,
        suggestionKey: suggestion.suggestionKey,
      });
      setAcceptedKeys((current) => {
        const next = new Set(current);
        next.add(suggestion.suggestionKey);
        return next;
      });
      onSuggestionAccepted(acceptResponse);
      setLiveMessage(acceptResponse.reused ? copy.acceptToastReused : copy.acceptToast);
    } catch (caughtError: unknown) {
      const serverMessage = typeof caughtError === "object"
        && caughtError
        && "response" in caughtError
        ? String(
            ((caughtError as { response?: { data?: { message?: string } } }).response?.data?.message)
            || copy.acceptError
          )
        : copy.acceptError;
      setAcceptError(serverMessage);
    } finally {
      setSubmittingKey(null);
    }
  }, [collectionId, copy, onSuggestionAccepted, response, submittingKey]);

  const handleDismiss = useCallback((suggestion: GraphAnalysisMissingLink) => {
    setDismissedKeys((current) => {
      const next = new Set(current);
      next.add(suggestion.suggestionKey);
      return next;
    });
  }, []);

  const handleFeedback = useCallback(
    async (suggestionKey: string, rating: -1 | 1) => {
      if (!response?.meta.invocationId || submittingFeedbackKey) {
        return;
      }
      const nextRating = feedbackStateByKey[suggestionKey] === rating ? 0 : rating;
      setSubmittingFeedbackKey(suggestionKey);
      try {
        await aiApi.submitFeedback({
          invocationId: response.meta.invocationId,
          rating: nextRating,
          suggestionKey,
        });
        setFeedbackStateByKey((current) => ({
          ...current,
          [suggestionKey]: nextRating === 0 ? null : nextRating,
        }));
      } finally {
        setSubmittingFeedbackKey(null);
      }
    },
    [feedbackStateByKey, response?.meta.invocationId, submittingFeedbackKey]
  );

  const insightKindLabel = useCallback(
    (kind: GraphAnalysisStructuralInsight["kind"]): string => {
      switch (kind) {
        case "cluster":
          return copy.insightKindCluster;
        case "feedback_loop":
          return copy.insightKindFeedbackLoop;
        case "chain":
          return copy.insightKindChain;
        case "isolate":
          return copy.insightKindIsolate;
        case "bridge_gap":
        default:
          return copy.insightKindBridgeGap;
      }
    },
    [copy]
  );

  const confidenceLabel = useCallback(
    (confidence: "low" | "medium" | "high"): string => {
      switch (confidence) {
        case "high":
          return copy.confidenceHigh;
        case "medium":
          return copy.confidenceMedium;
        case "low":
        default:
          return copy.confidenceLow;
      }
    },
    [copy]
  );

  const renderedSuggestions = response?.missingLinks.filter(
    (entry) => !dismissedKeys.has(entry.suggestionKey)
  ) ?? [];
  const groupedMissingLinks = groupMissingLinks(renderedSuggestions, {
    bridge: copy.missingLinkGroupBridge,
    downstream: copy.missingLinkGroupDownstream,
    lever: copy.missingLinkGroupLever,
    upstream: copy.missingLinkGroupUpstream,
  });

  const truncatedCount = response?.meta.truncated
    ? Number(response.analysis.droppedNodes || 0) + Number(response.analysis.droppedEdges || 0)
    : 0;

  return (
    <aside
      className="pointer-events-auto absolute right-4 top-4 z-20 flex max-h-[calc(100%-2rem)] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-eoe-border-main bg-eoe-bg-modal/95 shadow-lg backdrop-blur dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal/95"
      aria-label={copy.panelTitle}
      role="region"
    >
      <header className="flex items-start justify-between gap-3 border-b border-eoe-border-main p-4 dark:border-eoe-dark-border-main">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-eoe-blue">
            <Sparkles className="size-3.5" aria-hidden />
            {copy.panelTitle}
          </p>
          <h2 className="mt-1 text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
            {copy.panelSubtitle}
          </h2>
          {response?.meta.awardCost ? (
            <p className="mt-1.5 text-[11px] text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {ui.thisCostsAwards(response.meta.awardCost)}
            </p>
          ) : null}
        </div>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          aria-label={copy.closeLabel}
          className="rounded-full p-1 text-eoe-text-sub transition-colors hover:bg-eoe-icon hover:text-eoe-text dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-icon dark:hover:text-eoe-dark-text"
        >
          <X className="size-4" aria-hidden />
        </button>
      </header>

      <div className="flex items-center gap-2 border-b border-eoe-border-main p-3 dark:border-eoe-dark-border-main">
        <button
          type="button"
          onClick={() => void runAnalyze()}
          disabled={!canAnalyze || isLoading}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${
            !canAnalyze || isLoading
              ? "btn-primary-disabled cursor-not-allowed opacity-60"
              : "btn-secondary"
          }`}
          aria-label={response ? copy.reanalyzeAction : copy.analyzeAction}
        >
          {isLoading ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="size-4" aria-hidden />
          )}
          <span>
            {isLoading
              ? copy.analyzingLabel
              : response
                ? copy.reanalyzeAction
                : copy.analyzeAction}
          </span>
        </button>
        {!canAnalyze ? (
          <p className="text-xs italic text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {copy.notEnoughNodes}
          </p>
        ) : null}
      </div>

      {loadError ? (
        <div className="border-b border-eoe-border-main px-4 py-3 text-sm text-warning-red dark:border-eoe-dark-border-main dark:text-dark-warning-red" role="alert">
          {loadError}
        </div>
      ) : null}

      {truncatedCount > 0 ? (
        <div className="border-b border-eoe-border-main bg-eoe-bg-hover px-4 py-2 text-[11px] text-eoe-text-sub dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-hover dark:text-eoe-dark-text-sub">
          {copy.truncatedBanner(truncatedCount)}
        </div>
      ) : null}

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {!response && !isLoading && !loadError ? (
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {copy.emptyState}
          </p>
        ) : null}

        {response ? (
          <div className="flex flex-col gap-5">
            <section aria-labelledby="graph-analysis-summary">
              <h3
                id="graph-analysis-summary"
                className="text-xs font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub"
              >
                {copy.sectionSummary}
              </h3>
              <p className="mt-2 text-sm text-eoe-text dark:text-eoe-dark-text">
                {response.overallSummary}
              </p>
            </section>

            <section aria-labelledby="graph-analysis-insights">
              <h3
                id="graph-analysis-insights"
                className="text-xs font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub"
              >
                {copy.sectionInsights}
              </h3>
              {response.structuralInsights.length === 0 ? (
                <p className="mt-2 text-xs italic text-eoe-text-sub dark:text-eoe-dark-text-sub">
                  {copy.noInsights}
                </p>
              ) : (
                <ul className="mt-2 flex flex-col gap-2">
                  {response.structuralInsights.map((insight) => (
                    <li
                      key={`${insight.kind}-${insight.title}`}
                      className="rounded-xl border border-eoe-border-main bg-eoe-bg/70 p-3 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg/50"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-eoe-blue/10 px-2 py-0.5 text-[11px] font-semibold text-eoe-blue">
                          {insightKindLabel(insight.kind)}
                        </span>
                        <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                          {insight.title}
                        </p>
                      </div>
                      <p className="mt-1.5 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {insight.description}
                      </p>
                      {onFocusNode && insight.nodeIds.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {insight.nodeIds.map((nodeId) => (
                            <button
                              key={nodeId}
                              type="button"
                              onClick={() => onFocusNode(nodeId)}
                              className="rounded-full border border-eoe-border-main px-2 py-0.5 text-[11px] text-eoe-text-sub transition-colors hover:text-eoe-text dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
                            >
                              {`#${nodeId}`}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section aria-labelledby="graph-analysis-root-causes">
              <h3
                id="graph-analysis-root-causes"
                className="text-xs font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub"
              >
                {copy.sectionRootCauses}
              </h3>
              {response.rootCauseCandidates.length === 0 ? (
                <p className="mt-2 text-xs italic text-eoe-text-sub dark:text-eoe-dark-text-sub">
                  {copy.noRootCauses}
                </p>
              ) : (
                <ul className="mt-2 flex flex-col gap-2">
                  {response.rootCauseCandidates.map((candidate) => (
                    <li
                      key={`${candidate.nodeId}-${candidate.confidence}`}
                      className="rounded-xl border border-eoe-border-main bg-eoe-bg/70 p-3 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg/50"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onFocusNode?.(candidate.nodeId)}
                          className="rounded-full bg-purple-500/15 px-2 py-0.5 text-[11px] font-semibold text-purple-700 dark:text-purple-300"
                        >
                          {candidate.nodeName}
                        </button>
                        <span className="text-[11px] text-eoe-text-sub dark:text-eoe-dark-text-sub">
                          {confidenceLabel(candidate.confidence)}
                        </span>
                      </div>
                      <p className="mt-1.5 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {candidate.rationale}
                      </p>
                      {candidate.evidence.length > 0 ? (
                        <div className="mt-2">
                          <SourceChips
                            label={ui.sourceLabel}
                            sources={candidate.evidence}
                          />
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section aria-labelledby="graph-analysis-missing-links">
              <h3
                id="graph-analysis-missing-links"
                className="text-xs font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub"
              >
                {copy.sectionMissingLinks}
              </h3>
              {acceptError ? (
                <p className="mt-2 text-xs text-warning-red dark:text-dark-warning-red" role="alert">
                  {acceptError}
                </p>
              ) : null}
              {groupedMissingLinks.length === 0 ? (
                <p className="mt-2 text-xs italic text-eoe-text-sub dark:text-eoe-dark-text-sub">
                  {copy.noSuggestions}
                </p>
              ) : (
                <div className="mt-2 flex flex-col gap-4">
                  {groupedMissingLinks.map((group) => (
                    <div key={group.kind}>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {group.groupLabel}
                      </p>
                      <ul className="mt-1.5 flex flex-col gap-2">
                        {group.items.map((suggestion) => {
                          const isAccepted = acceptedKeys.has(suggestion.suggestionKey);
                          const isSubmitting = submittingKey === suggestion.suggestionKey;
                          const feedbackState = feedbackStateByKey[suggestion.suggestionKey] ?? null;
                          const isFeedbackSubmitting = submittingFeedbackKey === suggestion.suggestionKey;
                          const anchorName = suggestion.anchorNodeName || `#${suggestion.anchorNodeId}`;
                          const directionLabel = suggestion.direction === "influences"
                            ? copy.directionInfluences
                            : copy.directionInfluencedBy;
                          return (
                            <li
                              key={suggestion.suggestionKey}
                              className={`rounded-xl border p-3 transition-colors ${
                                isAccepted
                                  ? "border-green-500/60 bg-green-500/5"
                                  : "border-eoe-border-main bg-eoe-bg/70 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg/50"
                              }`}
                            >
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                                    {suggestion.wikipediaTitle}
                                  </p>
                                  {suggestion.suggestedDescription ? (
                                    <p className="mt-1 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                      {suggestion.suggestedDescription}
                                    </p>
                                  ) : null}
                                  <div
                                    className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-eoe-text-sub dark:text-eoe-dark-text-sub"
                                    aria-label={`${copy.anchorLabel}: ${anchorName} ${directionLabel}`}
                                  >
                                    {suggestion.direction === "influences" ? (
                                      <>
                                        <span>{suggestion.wikipediaTitle}</span>
                                        <ArrowRight className="size-3" aria-hidden />
                                        <button
                                          type="button"
                                          onClick={() => onFocusNode?.(suggestion.anchorNodeId)}
                                          className="rounded-full bg-eoe-icon px-2 py-0.5 text-[11px] font-semibold text-eoe-text-sub hover:text-eoe-text dark:bg-eoe-dark-icon dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
                                        >
                                          {anchorName}
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => onFocusNode?.(suggestion.anchorNodeId)}
                                          className="rounded-full bg-eoe-icon px-2 py-0.5 text-[11px] font-semibold text-eoe-text-sub hover:text-eoe-text dark:bg-eoe-dark-icon dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
                                        >
                                          {anchorName}
                                        </button>
                                        <ArrowLeft className="size-3" aria-hidden />
                                        <span>{suggestion.wikipediaTitle}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="mt-2">
                                <EntityChips
                                  wikipediaTitle={suggestion.wikipediaTitle}
                                  wikipediaUrl={suggestion.wikipediaUrl}
                                  wikidataQid={suggestion.wikidataQid}
                                  wikidataChipLabel={copy.wikidataChip}
                                />
                              </div>
                              <p className="mt-2 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                {suggestion.rationale}
                              </p>
                              {suggestion.sources.length > 0 ? (
                                <div className="mt-2">
                                  <SourceChips
                                    label={ui.sourceLabel}
                                    sources={suggestion.sources}
                                  />
                                </div>
                              ) : null}
                              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  {!isAccepted ? (
                                    <button
                                      type="button"
                                      onClick={() => void handleAccept(suggestion)}
                                      disabled={isSubmitting}
                                      className="btn-primary inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                      {isSubmitting ? (
                                        <Loader2 className="size-3.5 animate-spin" aria-hidden />
                                      ) : null}
                                      <span>{isSubmitting ? copy.addingLabel : copy.addToGraphAction}</span>
                                    </button>
                                  ) : (
                                    <span className="text-xs font-semibold text-green-700 dark:text-green-300">
                                      {copy.acceptToast}
                                    </span>
                                  )}
                                  {!isAccepted ? (
                                    <button
                                      type="button"
                                      onClick={() => handleDismiss(suggestion)}
                                      className="btn-secondary rounded-full px-3 py-1.5 text-xs font-semibold"
                                    >
                                      {copy.dismissAction}
                                    </button>
                                  ) : null}
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => void handleFeedback(suggestion.suggestionKey, 1)}
                                    aria-label={ui.feedbackPositive}
                                    disabled={isFeedbackSubmitting}
                                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${
                                      feedbackState === 1
                                        ? "bg-green-600 text-white"
                                        : "border border-eoe-border-main text-eoe-text-sub dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub"
                                    }`}
                                  >
                                    <ThumbsUp className="size-3" aria-hidden />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handleFeedback(suggestion.suggestionKey, -1)}
                                    aria-label={ui.feedbackNegative}
                                    disabled={isFeedbackSubmitting}
                                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${
                                      feedbackState === -1
                                        ? "bg-red-600 text-white"
                                        : "border border-eoe-border-main text-eoe-text-sub dark:border-eoe-dark-border-main dark:text-eoe-dark-text-sub"
                                    }`}
                                  >
                                    <ThumbsDown className="size-3" aria-hidden />
                                  </button>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <p className="text-[11px] text-eoe-text-sub dark:text-eoe-dark-text-sub">
              {ui.aiDisclaimer}
            </p>
          </div>
        ) : null}
      </div>

      <LiveAnnouncer message={liveMessage} />
    </aside>
  );
};

export default GraphAnalysisPanel;
